import { useState, useRef, useEffect, useLayoutEffect, type ReactNode } from 'react';

/**
 * Tooltip / in-context explanation (card Cibmk2YuSlFB — JitsLab parity).
 *
 * An in-context explanation beside a control: tap to reveal on touch (no
 * hover-only), dismissible, never blocks the control it describes, and never
 * takes over the control's 44px tap floor — it sits beside it as its own
 * target. Rendered in-place (position relative to the trigger) so it cannot
 * overlap fixed chrome or the tab bar.
 *
 * Wrap a BARE trigger (a `<span className="pear-tip-trigger">ⓘ</span>`), not
 * the control itself: the control keeps its own semantics and its own tap
 * target, and this stays one extra tab stop rather than a nested button.
 *
 * Usage:
 *   <Tooltip label="Turning this off stops the agent from offering tags.">
 *     <Switch ... />
 *   </Tooltip>
 */
export function Tooltip({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  /* The bubble is centred on its trigger, so a trigger near either edge used
     to hang the bubble off the viewport — clipped on a phone (founder email
     2026-10-25, card o4i7UUbHTn5M). Measure it after it paints and shift it
     back inside; the CSS caps the width, this fixes the position. */
  const bubble = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const el = bubble.current;
    if (!el) return;
    el.style.marginLeft = '0px';
    const r = el.getBoundingClientRect();
    const pad = 8;
    let dx = 0;
    if (r.left < pad) dx = pad - r.left;
    else if (r.right > window.innerWidth - pad) dx = window.innerWidth - pad - r.right;
    if (dx !== 0) el.style.marginLeft = `${Math.round(dx)}px`;
  }, [open]);

  // Close on an outside tap and on Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    /* Escape belongs to the tooltip, not to whatever overlay or composer
       listens further up: without this the capture bar under it took the same
       Escape as "clear the draft" and closed itself (mG5eA32qztRg). */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      e.stopPropagation();
    };
    /* CAPTURE phase, deliberately: React's synthetic keydown runs on the
       container's bubble path, so a bubble-phase listener here would only fire
       AFTER the composer had already taken the key (the capture bar cleared
       itself and closed on an Escape meant for the tip). Capturing at the
       document stops the event before it reaches React at all. */
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <span
      ref={wrap}
      className="pear-tooltip"
      // Focusable and named: an explanation a keyboard or screen-reader user
      // cannot reach is not an explanation (card Cibmk2YuSlFB). Tooltip wraps
      // a BARE trigger — never an interactive control, which keeps the role
      // honest and stops the tap target from doubling up.
      tabIndex={0}
      role="button"
      aria-label={label}
      aria-expanded={open}
      onClick={() => setOpen(o => !o)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen(o => !o)}
    >
      {children}
      {open && (
        <span className="pear-tooltip-bubble" role="tooltip" ref={bubble}>
          {label}
        </span>
      )}
    </span>
  );
}
